import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  MapPin, Trash2, X, Search, Radio, Loader2, Plus, Video, ImagePlus, Pencil,
  GripVertical,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates,
  useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import {
  useGetSpotsQuery,
  useCreateSpotMutation,
  useUpdateSpotMutation,
  useDeleteSpotMutation,
  useReorderSpotsMutation,
  useUploadVideoMutation,
  useUploadImagesMutation,
} from "../store/api/adminApi";
import { useToast } from "../components/Toast";

/** Shared middle cells for a spot row (name / media / coords / range). */
function SpotCells({ spot }) {
  return (
    <>
      <div className="min-w-0">
        <p className="text-[13px] font-manrope-bold text-gray-800 truncate">{spot.name}</p>
        {spot.address && (
          <p className="text-[11px] font-manrope-regular text-gray-400 truncate max-w-[180px]">
            {spot.address}
          </p>
        )}
      </div>

      <div className="pr-4 flex flex-col gap-1.5">
        {spot.videoUrl ? (
          <video src={spot.videoUrl} controls className="w-full max-w-[180px] h-[72px] rounded-lg object-contain bg-black" />
        ) : (
          <div className="flex items-center gap-1.5 text-gray-300">
            <Video size={14} strokeWidth={1.5} />
            <span className="text-[11px] font-manrope-regular">No video</span>
          </div>
        )}
        {Array.isArray(spot.images) && spot.images.length > 0 && (
          <div className="flex items-center gap-1.5 max-w-[180px]">
            <div className="flex -space-x-1.5">
              {spot.images.slice(0, 4).map((url) => (
                <img key={url} src={url} alt="" className="w-6 h-6 rounded-full object-cover border border-white bg-gray-100" />
              ))}
            </div>
            <span className="text-[11px] font-manrope-semibold text-gray-500">
              {spot.images.length} {spot.images.length === 1 ? "image" : "images"}
            </span>
          </div>
        )}
      </div>

      <div>
        {spot.location?.lat != null ? (
          <div>
            <p className="text-[11px] font-manrope-regular text-gray-500">{Number(spot.location.lat).toFixed(5)},</p>
            <p className="text-[11px] font-manrope-regular text-gray-500">{Number(spot.location.lng).toFixed(5)}</p>
          </div>
        ) : (
          <span className="text-[11px] font-manrope-regular text-gray-300">—</span>
        )}
      </div>

      <div>
        {spot.areaRange != null ? (
          <div className="flex items-center gap-1.5">
            <Radio size={11} className="text-primary flex-shrink-0" strokeWidth={1.8} />
            <span className="text-[12px] font-manrope-semibold text-gray-700">{spot.areaRange} km</span>
          </div>
        ) : (
          <span className="text-[11px] font-manrope-regular text-gray-300">—</span>
        )}
      </div>
    </>
  );
}

const ROW_GRID = "grid grid-cols-[30px_38px_1.7fr_1.7fr_1.1fr_0.9fr_0.9fr] gap-2 px-5 items-center";

/** Row action buttons (edit / delete). */
function RowActions({ spot, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => onEdit(spot)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all" aria-label="Edit spot">
        <Pencil size={13} className="text-gray-700" strokeWidth={1.8} />
      </button>
      <button onClick={() => onDelete(spot)} className="w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:opacity-80 transition-all shadow-sm" aria-label="Delete spot">
        <Trash2 size={13} className="text-white" strokeWidth={1.8} />
      </button>
    </div>
  );
}

/** A draggable spot row (drag handle reorders; number is its tour position). */
function SortableSpotRow({ spot, position, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: spot.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.12)" : undefined,
    background: isDragging ? "#fff" : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className={`${ROW_GRID} py-3.5 border-b border-gray-50 hover:bg-gray-50/50 transition-colors`}>
      <button
        {...attributes}
        {...listeners}
        className="w-6 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Drag to reorder ${spot.name}`}
        title="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>
      <span className="text-[13px] font-manrope-bold text-primary tabular-nums">{position}</span>
      <SpotCells spot={spot} />
      <RowActions spot={spot} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

const MAPBOX_TOKEN =
  "pk.eyJ1IjoidmlnNzcxOWFudG8iLCJhIjoiY21wMTg0a2t2MDJmNTJzb2x6MDFyazEwMSJ9.UkauDvpPrPSqymE795UYkA";
const MAP_STYLE = "mapbox://styles/mapbox/streets-v12";
const DEFAULT_CENTER = { lat: 12.1696, lng: -68.2837 };

const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));

/** Reverse-geocode a point to a human address via the Mapbox Geocoding API. */
async function reverseGeocode(lng, lat) {
  const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  try {
    const url =
      `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}` +
      `&limit=1&access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(url);
    if (!res.ok) return fallback;
    const data = await res.json();
    const f = data?.features?.[0];
    return f?.properties?.full_address || f?.properties?.name || fallback;
  } catch {
    return fallback;
  }
}

/** Forward-geocode a search query, biased toward Bonaire. */
async function forwardGeocode(query) {
  try {
    const url =
      `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(query)}` +
      `&limit=5&proximity=${DEFAULT_CENTER.lng},${DEFAULT_CENTER.lat}` +
      `&access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.features || [])
      .map((f) => ({
        id: f.id,
        name: f.properties?.name || f.properties?.full_address || "Unnamed",
        address: f.properties?.full_address || f.properties?.place_formatted || "",
        lng: f.geometry?.coordinates?.[0],
        lat: f.geometry?.coordinates?.[1],
      }))
      .filter((r) => r.lng != null && r.lat != null);
  } catch {
    return [];
  }
}

/** GeoJSON polygon approximating a circle of `radiusKm` around [lng, lat]. */
function circlePolygon(lng, lat, radiusKm, steps = 64) {
  const coords = [];
  const distX = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180));
  const distY = radiusKm / 110.574;
  for (let i = 0; i < steps; i++) {
    const theta = (i / steps) * (2 * Math.PI);
    coords.push([lng + distX * Math.cos(theta), lat + distY * Math.sin(theta)]);
  }
  coords.push(coords[0]);
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [coords] }, properties: {} };
}

/* ─── Map picker modal (Mapbox GL) ─── */
function MapPickerModal({ onClose, onConfirm, existingSpots = [], excludeSpotId = null, initial = null }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);          // the selected-location marker
  const otherMarkersRef = useRef([]);      // existing-spot markers
  const pickPointRef = useRef(null);       // stable ref to latest pickPoint

  // When editing a spot we get its current pin so the map opens ON that point
  // with the marker already dropped — the admin can see where it is and just
  // nudge it, instead of starting from a blank Bonaire view.
  const initialPoint =
    initial && Number.isFinite(Number(initial.lat)) && Number.isFinite(Number(initial.lng))
      ? { lat: Number(initial.lat), lng: Number(initial.lng), address: initial.address || "" }
      : null;

  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(
    initialPoint ? { lat: initialPoint.lat, lng: initialPoint.lng } : null,
  ); // { lat, lng }
  const [address, setAddress] = useState(initialPoint?.address || "");
  const [loadingAddress, setLoadingAddress] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const otherSpots = useMemo(
    () =>
      (existingSpots || [])
        .filter((s) => (s._id || s.id) !== excludeSpotId)
        .filter((s) => s?.location?.lat != null && s?.location?.lng != null)
        .map((s) => ({
          id: s._id || s.id,
          name: s.name,
          address: s.address || "",
          lat: Number(s.location.lat),
          lng: Number(s.location.lng),
          areaRange: Number(s.areaRange) || 0,
        })),
    [existingSpots, excludeSpotId],
  );

  // Place / move the pin and resolve its address.
  const pickPoint = useCallback(async (lng, lat, knownAddress) => {
    setSelected({ lat, lng });
    setResults([]);
    const map = mapRef.current;
    if (map) {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const m = new mapboxgl.Marker({ color: "#e11d48", draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);
        m.on("dragend", () => {
          const p = m.getLngLat();
          pickPointRef.current?.(p.lng, p.lat);
        });
        markerRef.current = m;
      }
    }
    if (knownAddress) {
      setAddress(knownAddress);
      return;
    }
    setLoadingAddress(true);
    const addr = await reverseGeocode(lng, lat);
    setAddress(addr);
    setLoadingAddress(false);
  }, []);
  pickPointRef.current = pickPoint;

  // Init the map once.
  useEffect(() => {
    if (!mapContainerRef.current) return undefined;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center: initialPoint
        ? [initialPoint.lng, initialPoint.lat]
        : [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
      zoom: initialPoint ? 15 : 11,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.on("click", (e) => pickPointRef.current?.(e.lngLat.lng, e.lngLat.lat));
    map.on("load", () => {
      setReady(true);
      // Drop the existing pin (draggable, address already known) so an edit
      // starts from the spot's current location instead of nothing.
      if (initialPoint) {
        pickPointRef.current?.(initialPoint.lng, initialPoint.lat, initialPoint.address || undefined);
      }
    });
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      otherMarkersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw existing spots (markers + range circles) once the style is loaded.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return undefined;

    const fc = {
      type: "FeatureCollection",
      features: otherSpots
        .filter((s) => s.areaRange > 0)
        .map((s) => circlePolygon(s.lng, s.lat, s.areaRange)),
    };
    if (map.getSource("spot-ranges")) {
      map.getSource("spot-ranges").setData(fc);
    } else {
      map.addSource("spot-ranges", { type: "geojson", data: fc });
      map.addLayer({
        id: "spot-ranges-fill", type: "fill", source: "spot-ranges",
        paint: { "fill-color": "#3b82f6", "fill-opacity": 0.08 },
      });
      map.addLayer({
        id: "spot-ranges-line", type: "line", source: "spot-ranges",
        paint: { "line-color": "#3b82f6", "line-opacity": 0.4, "line-width": 1 },
      });
    }

    otherMarkersRef.current.forEach((m) => m.remove());
    otherMarkersRef.current = otherSpots.map((s) => {
      const el = document.createElement("div");
      el.style.cssText =
        "width:14px;height:14px;border-radius:9999px;background:#3b82f6;" +
        "border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:pointer";
      const popup = new mapboxgl.Popup({ offset: 14, closeButton: false }).setHTML(
        `<div style="padding:2px 4px;max-width:220px">
           <p style="font-weight:700;font-size:13px;color:#111;margin:0">${escapeHtml(s.name)}</p>
           ${s.address ? `<p style="font-size:11px;color:#666;margin:4px 0 0">${escapeHtml(s.address)}</p>` : ""}
           ${s.areaRange > 0 ? `<p style="font-size:11px;color:#888;margin:4px 0 0">Range: ${s.areaRange} km</p>` : ""}
         </div>`,
      );
      return new mapboxgl.Marker({ element: el })
        .setLngLat([s.lng, s.lat])
        .setPopup(popup)
        .addTo(map);
    });

    return () => {
      otherMarkersRef.current.forEach((m) => m.remove());
      otherMarkersRef.current = [];
    };
  }, [ready, otherSpots]);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    const r = await forwardGeocode(query.trim());
    setResults(r);
    setSearching(false);
  };

  const chooseResult = (r) => {
    setQuery(r.name);
    setResults([]);
    if (mapRef.current) mapRef.current.flyTo({ center: [r.lng, r.lat], zoom: 15 });
    pickPoint(r.lng, r.lat, r.address || r.name);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[680px] overflow-hidden flex flex-col" style={{ height: 520 }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-[15px] font-manrope-bold text-gray-900">Pin a location</h2>
            <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">
              {initialPoint
                ? "Drag the red pin or click the map to move this spot"
                : "Search or click the map to set a spot"}
              {otherSpots.length > 0 && ` · ${otherSpots.length} existing ${otherSpots.length === 1 ? "spot" : "spots"} shown in blue`}
            </p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 relative">
          {/* Search box overlay */}
          <div className="absolute top-3 left-3 z-10 w-[280px]">
            <form onSubmit={runSearch} className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a place or address"
                className="w-full pl-9 pr-3 py-2 bg-white rounded-xl shadow-md text-[12px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none border border-gray-100"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {searching ? (
                  <Loader2 size={13} className="animate-spin text-gray-400" />
                ) : (
                  <Search size={13} className="text-gray-400" />
                )}
              </div>
            </form>
            {results.length > 0 && (
              <div className="mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => chooseResult(r)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <p className="text-[12px] font-manrope-semibold text-gray-800 truncate">{r.name}</p>
                    {r.address && (
                      <p className="text-[10px] font-manrope-regular text-gray-400 truncate">{r.address}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={mapContainerRef} className="w-full h-full" />

          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 gap-2">
              <Loader2 size={18} className="animate-spin text-gray-400" />
              <p className="text-[13px] font-manrope-regular text-gray-400">Loading map…</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          <div className="flex-1 mr-4 min-w-0">
            {selected ? (
              <div>
                <p className="text-[11px] font-manrope-semibold text-gray-400 mb-0.5">Selected location</p>
                <p className="text-[12px] font-manrope-regular text-gray-700 truncate">
                  {loadingAddress ? "Resolving address…" : address}
                </p>
                <p className="text-[10px] font-manrope-regular text-gray-400 mt-0.5">
                  Lat: {selected.lat.toFixed(6)} · Lng: {selected.lng.toFixed(6)}
                </p>
              </div>
            ) : (
              <p className="text-[12px] font-manrope-regular text-gray-400">Click on the map to drop a pin</p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={() => { if (selected) { onConfirm({ ...selected, address }); onClose(); } }}
              disabled={!selected}
              className="px-4 py-2 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 disabled:opacity-40 transition-all"
            >
              Confirm location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Delete confirm modal ─── */
function DeleteModal({ spot, onCancel, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">Delete spot?</h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1 leading-relaxed">
          You are about to delete{" "}
          <span className="font-manrope-semibold text-gray-700">{spot?.name}</span>.
          This action cannot be undone.
        </p>
        <div className="flex gap-3 w-full mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Edit spot modal ─── */
function EditSpotModal({ spot, onClose, onSaved }) {
  const [name, setName] = useState(spot.name || "");
  const [areaRange, setAreaRange] = useState(String(spot.areaRange ?? ""));
  const [location, setLocation] = useState({
    lat: spot.location?.lat,
    lng: spot.location?.lng,
    address: spot.address || "",
  });
  const initialMediaType = (spot.images && spot.images.length > 0 && !spot.videoUrl) ? "images" : "video";
  const [mediaType, setMediaType] = useState(initialMediaType);
  const [videoUrl, setVideoUrl] = useState(spot.videoUrl || "");
  const [videoPreview, setVideoPreview] = useState(spot.videoUrl || "");
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [images, setImages] = useState(Array.isArray(spot.images) ? [...spot.images] : []);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [formError, setFormError] = useState("");

  const videoInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  const [uploadVideo] = useUploadVideoMutation();
  const [uploadImages] = useUploadImagesMutation();
  const [updateSpot, { isLoading: isSaving }] = useUpdateSpotMutation();
  const { data: allSpotsData } = useGetSpotsQuery();
  const allSpots = allSpotsData?.data || [];

  const switchMediaType = (next) => {
    if (next === mediaType) return;
    if (next === "video") {
      setImages([]);
      if (imagesInputRef.current) imagesInputRef.current.value = "";
    } else {
      setVideoUrl("");
      setVideoPreview("");
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
    setFormError("");
    setMediaType(next);
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const objUrl = URL.createObjectURL(file);
    setVideoPreview(objUrl);
    setVideoUrl("");
    setIsUploadingVideo(true);
    setFormError("");
    try {
      const fd = new FormData();
      fd.append("video", file);
      const res = await uploadVideo(fd).unwrap();
      setVideoUrl(res.data?.url || res.url || "");
    } catch (err) {
      setFormError(err?.data?.error?.message || "Video upload failed.");
      setVideoPreview("");
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleImagesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setIsUploadingImages(true);
    setFormError("");
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("images", file));
      const res = await uploadImages(fd).unwrap();
      const urls = (res.data?.images || res.images || []).map((img) => img.url).filter(Boolean);
      setImages((prev) => [...prev, ...urls]);
    } catch (err) {
      setFormError(err?.data?.error?.message || "Image upload failed.");
    } finally {
      setIsUploadingImages(false);
      if (imagesInputRef.current) imagesInputRef.current.value = "";
    }
  };

  const removeImage = (url) => setImages((prev) => prev.filter((u) => u !== url));

  const handleSave = async () => {
    setFormError("");
    if (!name.trim()) { setFormError("Spot name is required."); return; }
    if (!location || location.lat == null || location.lng == null) {
      setFormError("Please pin a location on the map.");
      return;
    }
    if (!areaRange || Number(areaRange) <= 0) {
      setFormError("Area range must be a positive number.");
      return;
    }
    if (mediaType === "video" && !videoUrl) {
      setFormError("Please upload a video for this spot.");
      return;
    }
    if (mediaType === "images" && images.length === 0) {
      setFormError("Please upload at least one image for this spot.");
      return;
    }
    try {
      await updateSpot({
        id: spot.id,
        name: name.trim(),
        location: { lat: location.lat, lng: location.lng },
        address: location.address || "",
        areaRange: Number(areaRange),
        videoUrl: mediaType === "video" ? videoUrl : "",
        images: mediaType === "images" ? images : [],
      }).unwrap();
      onSaved();
    } catch (err) {
      setFormError(err?.data?.error?.message || "Failed to update spot.");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-[680px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="text-[15px] font-manrope-bold text-gray-900">Edit spot</h2>
              <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">Update the details for this spot</p>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
              <X size={15} className="text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Spot location</label>
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular hover:border-primary/40 transition-all text-left"
                >
                  <span className={`truncate pr-2 ${location?.lat != null ? "text-gray-700" : "text-gray-400"}`}>
                    {location?.lat != null
                      ? (location.address || `${Number(location.lat).toFixed(5)}, ${Number(location.lng).toFixed(5)}`)
                      : "Pin on the map"}
                  </span>
                  <MapPin size={15} className="text-primary flex-shrink-0" strokeWidth={1.8} />
                </button>
                {location?.lat != null && (
                  <p className="text-[10px] font-manrope-regular text-gray-400 mt-1 ml-1">
                    Lat: {Number(location.lat).toFixed(6)} · Lng: {Number(location.lng).toFixed(6)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Spot name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
              </div>

              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Area range (km)</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={areaRange}
                    onChange={(e) => setAreaRange(e.target.value)}
                    className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Radio size={14} className="text-primary" strokeWidth={1.8} />
                  </div>
                </div>
              </div>

            </div>

            {/* Media type toggle */}
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[12px] font-manrope-semibold text-gray-500">Spot media:</span>
              <div className="inline-flex bg-gray-100 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => switchMediaType("video")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-manrope-semibold transition-all ${
                    mediaType === "video" ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Video size={13} strokeWidth={2} />
                  Video
                </button>
                <button
                  type="button"
                  onClick={() => switchMediaType("images")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-manrope-semibold transition-all ${
                    mediaType === "images" ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <ImagePlus size={13} strokeWidth={2} />
                  Images
                </button>
              </div>
            </div>

            {mediaType === "video" && (
              <div className="mb-4">
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Spot video</label>
                <div
                  onClick={() => !isUploadingVideo && videoInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer
                    ${videoPreview ? "border-primary/30 bg-primary/5" : "border-gray-200 bg-gray-50 hover:border-primary/40 hover:bg-gray-100"}`}
                >
                  {videoPreview ? (
                    <div className="p-3 flex flex-col gap-2">
                      <video src={videoPreview} controls className="w-full max-h-48 rounded-lg object-contain bg-black" onClick={(e) => e.stopPropagation()} />
                      <div className="flex items-center justify-between px-1">
                        {isUploadingVideo ? (
                          <div className="flex items-center gap-1.5 text-primary">
                            <Loader2 size={13} className="animate-spin" />
                            <span className="text-[12px] font-manrope-semibold">Uploading…</span>
                          </div>
                        ) : videoUrl ? (
                          <span className="text-[12px] font-manrope-semibold text-green-600">✓ Uploaded</span>
                        ) : null}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setVideoPreview(""); setVideoUrl(""); if (videoInputRef.current) videoInputRef.current.value = ""; }}
                          className="text-[11px] font-manrope-semibold text-gray-400 hover:text-red-500 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Video size={18} className="text-primary" strokeWidth={1.8} />
                      </div>
                      <p className="text-[13px] font-manrope-semibold text-gray-600">Click to upload video</p>
                      <p className="text-[11px] font-manrope-regular text-gray-400">MP4, WebM, MOV, AVI — max 200 MB</p>
                    </div>
                  )}
                </div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
                  className="hidden"
                  onChange={handleVideoChange}
                />
              </div>
            )}

            {mediaType === "images" && (
              <div className="mb-4">
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Spot images</label>
                <div className="border-2 border-dashed border-gray-200 bg-gray-50 rounded-xl p-3">
                  {images.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3">
                      {images.map((url) => (
                        <div key={url} className="relative group rounded-lg overflow-hidden bg-black">
                          <img src={url} alt="" className="w-full h-20 object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(url)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-red-500 flex items-center justify-center transition-colors"
                            aria-label="Remove image"
                          >
                            <X size={11} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => !isUploadingImages && imagesInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-gray-200 hover:border-primary/40 hover:bg-white transition-all"
                    disabled={isUploadingImages}
                  >
                    {isUploadingImages ? (
                      <>
                        <Loader2 size={14} className="animate-spin text-primary" />
                        <span className="text-[12px] font-manrope-semibold text-primary">Uploading…</span>
                      </>
                    ) : (
                      <>
                        <ImagePlus size={14} className="text-primary" />
                        <span className="text-[12px] font-manrope-semibold text-gray-600">
                          {images.length === 0 ? "Click to add images" : "Add more images"}
                        </span>
                      </>
                    )}
                  </button>
                  <p className="text-[10px] font-manrope-regular text-gray-400 mt-2 ml-1">
                    JPG, PNG, WebP, GIF — up to 10 images, 5 MB each.
                  </p>
                </div>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={handleImagesChange}
                />
              </div>
            )}

            {formError && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">
                {formError}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isUploadingVideo || isUploadingImages}
              className="px-4 py-2 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 disabled:opacity-40 transition-all flex items-center gap-2"
            >
              {isSaving && <Loader2 size={13} className="animate-spin" />}
              {isSaving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {showMap && (
        <MapPickerModal
          onClose={() => setShowMap(false)}
          onConfirm={(loc) => setLocation(loc)}
          existingSpots={allSpots}
          excludeSpotId={spot._id || spot.id}
          initial={location}
        />
      )}
    </>
  );
}

/* ─── Main page ─── */
export default function Spots() {
  const user = useSelector((state) => state.auth.user);

  // Form state
  const [location, setLocation] = useState(null);
  const [spotName, setSpotName] = useState("");
  const [areaRange, setAreaRange] = useState("");
  const [mediaType, setMediaType] = useState("video"); // "video" | "images"
  const [videoUrl, setVideoUrl] = useState("");
  const [videoPreview, setVideoPreview] = useState(""); // local object URL for preview
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([]); // uploaded image URLs
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [formError, setFormError] = useState("");
  const videoInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  const switchMediaType = (next) => {
    if (next === mediaType) return;
    if (next === "video") {
      setImages([]);
      if (imagesInputRef.current) imagesInputRef.current.value = "";
    } else {
      setVideoUrl("");
      setVideoPreview("");
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
    setFormError("");
    setMediaType(next);
  };

  // UI state
  const [showMap, setShowMap] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState("");

  // API
  const { data, isLoading, isError } = useGetSpotsQuery();
  const [createSpot, { isLoading: isCreating }] = useCreateSpotMutation();
  const [deleteSpot, { isLoading: isDeleting }] = useDeleteSpotMutation();
  const [reorderSpots] = useReorderSpotsMutation();
  const [uploadVideo] = useUploadVideoMutation();
  const [uploadImages] = useUploadImagesMutation();
  const toast = useToast();

  const spots = data?.data || [];

  // Local, drag-reorderable copy of the list. Re-synced whenever the server
  // data changes (including our own optimistic reorder patch), so the on-screen
  // order and the cache never drift apart.
  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems(data?.data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Reordering only makes sense on the FULL list — a drag inside a filtered
  // search view would move items in an order the admin can't fully see. So we
  // disable dragging while searching and show a hint instead.
  const isSearching = search.trim().length > 0;
  const visible = isSearching
    ? items.filter((s) => s.name?.toLowerCase().includes(search.toLowerCase()))
    : items;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((s) => s.id === active.id);
    const newIndex = items.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next); // instant feedback
    reorderSpots(next.map((s) => s.id))
      .unwrap()
      .then(() => toast.success("Tour order updated."))
      .catch(() => toast.error("Couldn't save the new order. Reverted."));
  };

  const resetForm = () => {
    setSpotName("");
    setAreaRange("");
    setLocation(null);
    setVideoUrl("");
    setVideoPreview("");
    setImages([]);
    setMediaType("video");
    setFormError("");
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (imagesInputRef.current) imagesInputRef.current.value = "";
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Local preview
    const objUrl = URL.createObjectURL(file);
    setVideoPreview(objUrl);
    setVideoUrl("");
    setIsUploading(true);
    setFormError("");

    try {
      const fd = new FormData();
      fd.append("video", file);
      const res = await uploadVideo(fd).unwrap();
      setVideoUrl(res.data?.url || res.url || "");
    } catch (err) {
      setFormError(err?.data?.error?.message || "Video upload failed. Please try again.");
      setVideoPreview("");
    } finally {
      setIsUploading(false);
    }
  };

  const handleImagesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploadingImages(true);
    setFormError("");

    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("images", file));
      const res = await uploadImages(fd).unwrap();
      const urls = (res.data?.images || res.images || [])
        .map((img) => img.url)
        .filter(Boolean);
      setImages((prev) => [...prev, ...urls]);
    } catch (err) {
      setFormError(err?.data?.error?.message || "Image upload failed. Please try again.");
    } finally {
      setIsUploadingImages(false);
      if (imagesInputRef.current) imagesInputRef.current.value = "";
    }
  };

  const removeImage = (url) => {
    setImages((prev) => prev.filter((u) => u !== url));
  };

  const handleAdd = async () => {
    setFormError("");
    if (!spotName.trim()) { setFormError("Spot name is required."); return; }
    if (!location) { setFormError("Please pin a location on the map."); return; }
    if (!areaRange || Number(areaRange) <= 0) { setFormError("Area range must be a positive number."); return; }
    if (mediaType === "video" && !videoUrl) {
      setFormError("Please upload a video for this spot.");
      return;
    }
    if (mediaType === "images" && images.length === 0) {
      setFormError("Please upload at least one image for this spot.");
      return;
    }

    try {
      await createSpot({
        name: spotName.trim(),
        location: { lat: location.lat, lng: location.lng },
        address: location.address || "",
        areaRange: Number(areaRange),
        videoUrl: mediaType === "video" ? videoUrl : "",
        images: mediaType === "images" ? images : [],
      }).unwrap();
      resetForm();
      toast.success("Spot added successfully.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to create spot.");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSpot(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      toast.success("Spot deleted.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to delete spot.");
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">Spot Management</h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Add and manage all your location spots from here.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-manrope-bold text-gray-600">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">{user?.name}</span>
              <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">{user?.email}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 px-8 pb-8 flex flex-col gap-5">
          {/* Add spot form */}
          <div className="bg-white rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Plus size={16} className="text-primary" strokeWidth={2} />
              <h2 className="text-[15px] font-manrope-bold text-gray-900">Add new spot</h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Location picker */}
              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                  Spot location
                </label>
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular hover:border-primary/40 transition-all text-left"
                >
                  <span className={`truncate pr-2 ${location ? "text-gray-700" : "text-gray-400"}`}>
                    {location
                      ? location.address || `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
                      : "Pin on the map"}
                  </span>
                  <MapPin size={15} className="text-primary flex-shrink-0" strokeWidth={1.8} />
                </button>
                {location && (
                  <p className="text-[10px] font-manrope-regular text-gray-400 mt-1 ml-1">
                    Lat: {location.lat.toFixed(6)} · Lng: {location.lng.toFixed(6)}
                  </p>
                )}
              </div>

              {/* Spot name */}
              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                  Spot name
                </label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={spotName}
                  onChange={(e) => setSpotName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
              </div>

              {/* Area range */}
              <div>
                <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                  Area range (km)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="e.g. 0.5"
                    value={areaRange}
                    onChange={(e) => setAreaRange(e.target.value)}
                    className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Radio size={14} className="text-primary" strokeWidth={1.8} />
                  </div>
                </div>
                <p className="text-[10px] font-manrope-regular text-gray-400 mt-1 ml-1">
                  Radius in km to trigger playback
                </p>
              </div>

            </div>

            {/* Media type toggle */}
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[12px] font-manrope-semibold text-gray-500">Spot media:</span>
              <div className="inline-flex bg-gray-100 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => switchMediaType("video")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-manrope-semibold transition-all ${
                    mediaType === "video"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Video size={13} strokeWidth={2} />
                  Video
                </button>
                <button
                  type="button"
                  onClick={() => switchMediaType("images")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-manrope-semibold transition-all ${
                    mediaType === "images"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <ImagePlus size={13} strokeWidth={2} />
                  Images
                </button>
              </div>
            </div>

            {/* Video upload */}
            {mediaType === "video" && (
            <div className="mb-4">
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                Spot video
              </label>

              {/* Drop zone / file picker */}
              <div
                onClick={() => !isUploading && videoInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer
                  ${videoPreview ? "border-primary/30 bg-primary/5" : "border-gray-200 bg-gray-50 hover:border-primary/40 hover:bg-gray-100"}`}
              >
                {videoPreview ? (
                  <div className="p-3 flex flex-col gap-2">
                    <video
                      src={videoPreview}
                      controls
                      className="w-full max-h-48 rounded-lg object-contain bg-black"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center justify-between px-1">
                      {isUploading ? (
                        <div className="flex items-center gap-1.5 text-primary">
                          <Loader2 size={13} className="animate-spin" />
                          <span className="text-[12px] font-manrope-semibold">Uploading…</span>
                        </div>
                      ) : videoUrl ? (
                        <span className="text-[12px] font-manrope-semibold text-green-600">✓ Uploaded</span>
                      ) : null}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setVideoPreview(""); setVideoUrl(""); if (videoInputRef.current) videoInputRef.current.value = ""; }}
                        className="text-[11px] font-manrope-semibold text-gray-400 hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Video size={18} className="text-primary" strokeWidth={1.8} />
                    </div>
                    <p className="text-[13px] font-manrope-semibold text-gray-600">Click to upload video</p>
                    <p className="text-[11px] font-manrope-regular text-gray-400">MP4, WebM, MOV, AVI — max 200 MB</p>
                  </div>
                )}
              </div>

              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
                className="hidden"
                onChange={handleVideoChange}
              />
            </div>
            )}

            {/* Images upload */}
            {mediaType === "images" && (
            <div className="mb-4">
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                Spot images
              </label>

              <div className="border-2 border-dashed border-gray-200 bg-gray-50 rounded-xl p-3">
                {images.length > 0 ? (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3">
                    {images.map((url) => (
                      <div key={url} className="relative group rounded-lg overflow-hidden bg-black">
                        <img src={url} alt="" className="w-full h-20 object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(url)}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-red-500 flex items-center justify-center transition-colors"
                          aria-label="Remove image"
                        >
                          <X size={11} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => !isUploadingImages && imagesInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-gray-200 hover:border-primary/40 hover:bg-white transition-all"
                  disabled={isUploadingImages}
                >
                  {isUploadingImages ? (
                    <>
                      <Loader2 size={14} className="animate-spin text-primary" />
                      <span className="text-[12px] font-manrope-semibold text-primary">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <ImagePlus size={14} className="text-primary" />
                      <span className="text-[12px] font-manrope-semibold text-gray-600">
                        {images.length === 0 ? "Click to add images" : "Add more images"}
                      </span>
                    </>
                  )}
                </button>
                <p className="text-[10px] font-manrope-regular text-gray-400 mt-2 ml-1">
                  JPG, PNG, WebP, GIF — up to 10 images, 5 MB each. Shown as a looping carousel in the app.
                </p>
              </div>

              <input
                ref={imagesInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={handleImagesChange}
              />
            </div>
            )}

            {/* Error */}
            {formError && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl mb-4">
                {formError}
              </p>
            )}

            <button
              onClick={handleAdd}
              disabled={isCreating || isUploading || isUploadingImages}
              className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 disabled:opacity-60 transition-all bg-primary flex items-center justify-center gap-2"
            >
              {isCreating && <Loader2 size={16} className="animate-spin" />}
              {isCreating ? "Adding…" : "Add spot"}
            </button>
          </div>

          {/* Spots list */}
          <div className="bg-white rounded-2xl overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-50">
              <div className="mr-auto">
                <h2 className="text-[15px] font-manrope-bold text-gray-900">All spots</h2>
                <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">
                  {isSearching
                    ? "Clear the search to drag-reorder spots."
                    : "Drag the handle to reorder. The number is the position shown in the app."}
                </p>
              </div>
              <div className="relative flex-1 max-w-xs">
                <input
                  type="text"
                  placeholder="Search spots"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-4 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
              </div>
              <button className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center hover:opacity-90 transition-all flex-shrink-0">
                <Search size={15} className="text-white" strokeWidth={2} />
              </button>
            </div>

            {/* Table head */}
            <div className={`${ROW_GRID} py-3 border-b border-gray-50`}>
              <span />
              <span className="text-[12px] font-manrope-semibold text-gray-400">#</span>
              {["Spot name", "Video", "Coordinates", "Range", "Action"].map((col) => (
                <span key={col} className="text-[12px] font-manrope-semibold text-gray-400">{col}</span>
              ))}
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[13px] font-manrope-regular">Loading spots…</span>
              </div>
            )}

            {/* Error */}
            {isError && (
              <div className="flex items-center justify-center py-16 text-red-400 text-[13px] font-manrope-regular">
                Failed to load spots. Please try again.
              </div>
            )}

            {/* Empty */}
            {!isLoading && !isError && visible.length === 0 && (
              <div className="py-16 flex items-center justify-center">
                <p className="text-[13px] font-manrope-semibold text-gray-400">No spots found</p>
              </div>
            )}

            {/* Rows — sortable when browsing the full list, static while searching */}
            {!isLoading && !isError && visible.length > 0 && (
              <div className="max-h-[620px] overflow-y-auto">
                {isSearching ? (
                  visible.map((spot) => (
                    <div key={spot._id || spot.id} className={`${ROW_GRID} py-3.5 border-b border-gray-50 items-center`}>
                      <span className="w-6 h-8 flex items-center justify-center text-gray-200">
                        <GripVertical size={16} />
                      </span>
                      <span className="text-[13px] font-manrope-bold text-primary tabular-nums">
                        {items.findIndex((s) => s.id === spot.id) + 1}
                      </span>
                      <SpotCells spot={spot} />
                      <RowActions spot={spot} onEdit={setEditTarget} onDelete={setDeleteTarget} />
                    </div>
                  ))
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={items.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {items.map((spot, index) => (
                        <SortableSpotRow
                          key={spot.id}
                          spot={spot}
                          position={index + 1}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )}

            {/* Footer count */}
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-[12px] font-manrope-regular text-gray-500">
                Total <span className="font-manrope-bold text-gray-800">{items.length}</span> spots
                {isSearching && ` · ${visible.length} matching`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {showMap && (
        <MapPickerModal
          onClose={() => setShowMap(false)}
          onConfirm={(loc) => setLocation(loc)}
          existingSpots={spots}
          initial={location}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          spot={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}

      {editTarget && (
        <EditSpotModal
          spot={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            toast.success("Spot updated.");
          }}
        />
      )}
    </>
  );
}
